import * as React from "react";
import { useState, useEffect } from "react";
import _ from "lodash";
import {
  createStyles,
  Paper,
  Theme,
  makeStyles,
  Fab,
  TextField,
  Typography,
  InputAdornment,
  Switch,
  FormControlLabel,
  IconButton,
  Grid,
  Box,
} from "@material-ui/core";
import {
  ArrowBack as BackIcon,
  SaveOutlined as SaveIcon,
} from "@material-ui/icons";
import Loader from "./components/Loader";
import { AnvokError, String } from "lib";
import ItemComponents from "./components/ItemComponents";
import Packagings from "./item/Packagings";
import SelectItemType from "./components/SelectItemType";
import { useApiClient } from "lib/store";
import { useErrorHandler } from "ErrorHandler";
import useRouter from "use-react-router";
import { ApiItem, ApiItemComponent, ApiItemPackaging } from "lib/api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "50%",
      maxWidth: 500,
      margin: "auto",
      padding: theme.spacing(3),
    },
    input: { width: "100%", marginTop: 5, marginBottom: 5 },
    chip: {
      margin: theme.spacing(1),
    },
    chips: {
      display: "inline",
    },
    backbtn: {
      margin: theme.spacing(2),
      fontWeight: "bold",
      float: "left",
    },
  })
);

interface State {
  loading: boolean;
  ref: string;
  refError?: string;
  label: string;
  labelError?: string;
  altRefs: string;
  place: string;
  stock: string;
  stockReappro: string;
  weight: string;
  capacity: string;
  components: ApiItemComponent[];
  componentsRefErrors: { [i: number]: string };
  componentsQtyErrors: { [i: number]: string };
  packagings: ApiItemPackaging[];
  packagingsLabelErrors: { [i: number]: string };
  packagingsQtyErrors: { [i: number]: string };
  types: string[];
  cielCatFisc: string;
  tav: string;
  tavError?: string;
  disabled: boolean;
  nopicking: boolean;
}

const initialState: State = {
  ref: "",
  label: "",
  altRefs: "",
  place: "",
  stock: "",
  stockReappro: "",
  weight: "",
  capacity: "",
  components: [],
  componentsRefErrors: {},
  componentsQtyErrors: {},
  packagings: [],
  packagingsLabelErrors: {},
  packagingsQtyErrors: {},
  types: [],
  cielCatFisc: "",
  tav: "",
  disabled: false,
  nopicking: false,
  loading: false,
};

interface Params {
  id?: string;
}

const ItemEdit: React.FC = () => {
  const api = useApiClient();
  const errorHandler = useErrorHandler();
  const classes = useStyles();
  const { history, match } = useRouter<Params>();
  const { id } = match.params;

  const [state, setState] = useState<State>({
    ...initialState,
    loading: Boolean(id),
  });

  useEffect(() => {
    if (id) {
      api
        .GET("/api/items/{id}", { params: { path: { id } } })
        .then((r) => {
          if (r.error) {
            throw new AnvokError(r.error.message);
          }
          const item = r.data;
          if (item) {
            setState({
              ...initialState,
              ...item,
              stock:
                item.stock === undefined || item.stock === null
                  ? ""
                  : item.stock.toString(),
              stockReappro:
                item.stockReappro === undefined || item.stockReappro === null
                  ? ""
                  : item.stockReappro.toString(),
              altRefs: item.altRefs ? item.altRefs.join(", ") : "",
              weight: item.weight ? item.weight.toString() : "",
              capacity: item.capacity ? item.capacity.toString() : "",
              components: item.components ? item.components : [],
              packagings: item.packagings ? item.packagings : [],
              tav: item.tav ? item.tav.toString() : "",
              disabled: item.disabled ? item.disabled : false,
              nopicking: item.nopicking ? item.nopicking : false,
              loading: false,
            });
          } else {
            console.error(`Item not found #${id}`);
          }
        })
        .catch(errorHandler);
    }
  }, [id, api, errorHandler]);

  const updateState = (updates: Partial<State>) => {
    setState((prevState) => ({ ...prevState, ...updates }));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    updateState({ [name]: value });
  };

  const handleCheckChange =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      updateState({ [name]: event.target.checked });
    };

  const checkForm = (): boolean => {
    let refError: string | undefined = undefined;
    let labelError: string | undefined = undefined;
    let tavError: string | undefined = undefined;

    if (state.ref.length < 2) {
      refError = "La référence doit contenir au moins 2 charactères";
    }
    if (state.label.length < 4) {
      labelError = "Le libellé doit contenir au moins 4 charactères";
    }
    if (
      state.tav.trim() !== "" &&
      String.parseNumber(state.tav) === undefined
    ) {
      tavError = "La valeur doit être numérique";
    }

    const componentsRefErrors: { [i: number]: string } = {};
    const componentsQtyErrors: { [i: number]: string } = {};
    state.components.forEach((v, i) => {
      componentsRefErrors[i] = v.ref === "" ? "Référence requise" : "";
      componentsQtyErrors[i] = v.qty === 0 ? "Quantité requise" : "";
    });

    const packagingsLabelErrors: { [i: number]: string } = {};
    const packagingsQtyErrors: { [i: number]: string } = {};
    state.packagings.forEach((v, i) => {
      packagingsLabelErrors[i] = v.label === "" ? "Intitulé requis" : "";
      packagingsQtyErrors[i] = v.qty === 0 ? "Quantité requise" : "";
    });

    const hasError = ([] as string[])
      .concat(
        _.values(componentsRefErrors),
        _.values(componentsQtyErrors),
        _.values(packagingsLabelErrors),
        _.values(packagingsQtyErrors)
      )
      .some((v) => Boolean(v));

    updateState({
      refError,
      labelError,
      tavError,
      componentsRefErrors,
      componentsQtyErrors,
      packagingsLabelErrors,
      packagingsQtyErrors,
    });

    return !(refError || labelError || tavError || hasError);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!checkForm()) {
      return;
    }

    updateState({ loading: true });

    const item: Omit<
      ApiItem,
      "_id" | "created" | "modified" | "org" | "owner"
    > = {
      ref: state.ref.trim(),
      altRefs: state.altRefs
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v !== ""),
      label: state.label.trim(),
      place: state.place.trim(),
      stock: String.parseNumber(state.stock) ?? null,
      stockReappro: String.parseNumber(state.stockReappro) ?? null,
      types: state.types,
      weight: String.parseNumber(state.weight) ?? null,
      capacity: String.parseNumber(state.capacity) ?? null,
      components: state.components,
      packagings: state.packagings,
      cielCatFisc: state.cielCatFisc.trim(),
      tav: state.tav === "" ? undefined : String.parseNumber(state.tav),
      disabled: state.disabled,
      nopicking: state.nopicking,
    };

    if (id) {
      api
        .PUT(`/api/items/{id}`, { params: { path: { id } }, body: item })
        .then((r) => {
          if (r.error) {
            throw new AnvokError(r.error.message);
          }
          history.push("/items");
        })
        .catch(errorHandler);
    } else {
      api
        .POST("/api/items", { body: item })
        .then((r) => {
          if (r.error) {
            throw new AnvokError(r.error.message);
          }
          history.push("/items");
        })
        .catch(errorHandler)
        .finally(() => updateState({ loading: false }));
    }
  };

  const {
    loading,
    ref,
    refError,
    label,
    labelError,
    altRefs,
    place,
    stock,
    stockReappro,
    types,
    weight,
    capacity,
    components,
    componentsRefErrors,
    componentsQtyErrors,
    packagings,
    packagingsLabelErrors,
    packagingsQtyErrors,
    cielCatFisc,
    tav,
    tavError,
    disabled,
    nopicking,
  } = state;

  return (
    <Paper className={classes.root} elevation={1}>
      <Loader show={loading}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h2">
            <IconButton onClick={() => history.push("/items")}>
              <BackIcon />
            </IconButton>
            {id ? "Modification" : "Création"} article
          </Typography>
          <TextField
            className={classes.input}
            value={ref}
            error={!!refError}
            name="ref"
            onChange={handleChange}
            label="Référence"
            helperText={refError}
          />
          <TextField
            className={classes.input}
            value={label}
            error={!!labelError}
            name="label"
            onChange={handleChange}
            label="Libellé"
            helperText={labelError}
          />
          <SelectItemType
            className={classes.input}
            value={types}
            onChange={(types) => updateState({ types: types as string[] })}
          />
          <TextField
            className={classes.input}
            value={altRefs}
            name="altRefs"
            onChange={handleChange}
            label="Référence alternatives"
          />
          <TextField
            className={classes.input}
            value={place}
            name="place"
            onChange={handleChange}
            label="Emplacement"
          />
          <Box display="flex">
            <Box flexGrow={1}>
              <TextField
                className={classes.input}
                value={stock}
                name="stock"
                onChange={handleChange}
                label="Stock"
                fullWidth
              />
            </Box>
            <Box flexGrow={2}>
              <TextField
                className={classes.input}
                value={stockReappro}
                name="stockReappro"
                onChange={handleChange}
                label="Réapprovisionnement en cours"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">+</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Box>
          </Box>
          <TextField
            className={classes.input}
            value={weight}
            name="weight"
            onChange={handleChange}
            label="Poids"
            InputProps={{
              endAdornment: <InputAdornment position="end">g</InputAdornment>,
            }}
          />
          <TextField
            className={classes.input}
            value={capacity}
            name="capacity"
            onChange={handleChange}
            label="Contenance"
            InputProps={{
              endAdornment: <InputAdornment position="end">ml</InputAdornment>,
            }}
          />
          <TextField
            className={classes.input}
            value={cielCatFisc}
            name="cielCatFisc"
            onChange={handleChange}
            label="Libellé fiscal"
          />
          <TextField
            className={classes.input}
            value={tav}
            error={!!tavError}
            name="tav"
            onChange={handleChange}
            label="Titre alcoométrique volumique"
            helperText={tavError}
          />
          <Typography variant="h6">Composants</Typography>
          <ItemComponents
            value={components}
            refErrors={componentsRefErrors}
            qtyErrors={componentsQtyErrors}
            onChange={(value) => {
              updateState({ components: value });
            }}
          />
          <Typography variant="h6">Conditionnements</Typography>
          <Packagings
            value={packagings}
            labelErrors={packagingsLabelErrors}
            qtyErrors={packagingsQtyErrors}
            onChange={(packagings) => updateState({ packagings })}
          />

          <Grid container justify="space-between">
            <Grid item>
              <FormControlLabel
                control={
                  <Switch
                    checked={nopicking}
                    onChange={handleCheckChange("nopicking")}
                    value="checkedA"
                  />
                }
                label="Picking désactivé"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={disabled}
                    onChange={handleCheckChange("disabled")}
                    value="checkedA"
                  />
                }
                label="Article désactivé"
              />
            </Grid>
            <Grid item>
              <Fab type="submit" color="secondary">
                <SaveIcon />
              </Fab>
            </Grid>
          </Grid>
        </form>
      </Loader>
    </Paper>
  );
};

export default ItemEdit;
